/* T-562 / T-844 -- Reusable wall-builder component, version 2 (Martin's layout Option A, 2026-09-13).
 *
 * Renders, for any product carrying the `dpc-wall-builder` tag + a valid custom.modular_pieces
 * metafield (with its `climb` block), a full-width builder below the product grid: a wall picture
 * that stays in view (CSS sticky), a piece list with a real product photo per piece (lightbox),
 * steppers, and one multi-add basket bar. Nothing here reads a product handle to decide behaviour;
 * the frame geometry, picture caps, hints and placement parameters all come from the metafield.
 *
 * PICTURE vs BASKET: the picture shows at most `picture_cap` of each piece (`picture_cap_ext` once an
 * Extension Set is chosen). The basket quantity is never capped by the picture.
 *
 * PLACEMENT (planWall, pure, no DOM; exercised by REBUILD/probe/v2_slots_test.js):
 *   FIXED SLOT TABLES plus substitution rules (Martin, 2026-09-13; replaced the equal-spacing solver).
 *   Every number (uprights, sections, piece sizes, slots, caps, drawing box) lives in SLOT_TABLES
 *   below, keyed by the metafield's climb.table: layouts.standard without an Extension Set,
 *   layouts.extension with one. Each metafield piece names its table entry with `slot`. cm; x is a
 *   piece's left edge, y its BOTTOM, measured up from the frame bottom.
 *   Substitution: every small shelf after the first is a spare; with no Nest pictured the first spare
 *   takes the Nest place, then with no House the next spare takes the House place. A moved shelf
 *   leaves its own slot empty. Cushions go on pictured small shelves in shelf order, Hemp quantity
 *   first, one per shelf.

 * Cart wiring (unchanged from v1, reverse-engineered from theme.js; UNVERIFIED against a real cart
 * until the supervised Test check runs -- SHIP-PLAN.md section 4):
 *   - dispatch 'cart:prepare-bundled-sections' so CartDrawer adds its section id;
 *   - POST /cart/add.js {items, sections, sections_url};
 *   - on non-200 re-read via POST /cart/update.js {updates:{}} (still returns sections);
 *   - dispatch 'cart:change' {baseEvent:'variant:add', cart} so the drawer re-renders and opens.
 */
(function () {
  'use strict';

  // ── Placement: fixed slot tables (pure) ───────────────────────────────────────────────────────
  // THE ONE PLACE FOR LAYOUT NUMBERS. cm. x = left edge, y = BOTTOM of the piece, up from the frame
  // bottom. `at` names an upright (round pieces are centred on it) or a section (left edge).
  // layouts.standard is used without an Extension Set, layouts.extension whenever one is pictured.
  // Mirrors LAYOUT / wallHTML() in Martin's approved mockup (alto-builder-layouts.html, 2026-09-13).
  var SLOT_TABLES = {
    alto: {
      uprights: { U0: 1.6, U1: 46.8, U2: 92.0, U3: 137.2 },
      upright_w: 3.2,
      sections: { sec1: 3.2, sec2: 48.4, sec3: 94.4 },
      frame: { x: 0, w: 93.6, h: 185.6 },
      extension: { x: 93.6, w: 45.2, h: 185.6 },
      sizes: {
        shelf_small: { w: 42, h: 12 },
        shelf_big: { w: 87.2, h: 12 },
        house: { w: 42, h: 42 },
        nest: { w: 47.1, h: 49.6 },
        scratcher: { w: 47.6, h: 47.6 },
        cushion: { w: 40.4, h: 6.5 }
      },
      cushion_on_shelf: { dx: 0.8, dy: 1.6 },
      cushion_order: ['cushion_hemp', 'cushion_anthracite'],
      drawing_box: { xmin: -26, ymin: 0, ymax: 190 },
      layouts: {
        standard: {
          // pictured caps; the basket is never capped
          caps: { structure: 1, extension: 1, scratcher: 1, nest: 1, house: 1, shelf_small: 2, shelf_big: 1 },
          scratcher: [{ at: 'U0', y: 4.2 }],
          nest: [{ at: 'U0', y: 58, y_no_scratcher: 40 }],
          house: [{ at: 'sec2', y: 74 }],
          shelf_small: [{ at: 'sec2', y: 22 }, { at: 'sec1', y: 131 }],
          // where a spare small shelf goes when no Nest / no House is pictured (Nest place first)
          nest_place: { at: 'sec1', y: 58, y_no_scratcher: 40 },
          house_place: { at: 'sec2', y: 92 },
          shelf_big: { at: 'sec1', y: 164 },
          xmax: 100
        },
        extension: {
          caps: { structure: 1, extension: 1, scratcher: 1, nest: 2, house: 2, shelf_small: 2, shelf_big: 1 },
          scratcher: [{ at: 'U3', y: 4.2 }],
          nest: [{ at: 'U0', y: 52 }, { at: 'U0', y: 108 }],
          house: [{ at: 'sec3', y: 58 }, { at: 'sec3', y: 134 }],
          shelf_small: [{ at: 'sec2', y: 22 }, { at: 'sec2', y: 112 }],
          nest_place: { at: 'sec1', y: 64 },
          house_place: { at: 'sec3', y: 64 },
          shelf_big: { at: 'sec1', y: 164 },
          xmax: 165
        }
      }
    }
  };

  function r2(n) { return Math.round(n * 100) / 100; }

  function planWall(cfg, qtyOf, tableOverride) {
    var T = tableOverride || (cfg && cfg.climb && SLOT_TABLES[cfg.climb.table]);
    if (!T || !cfg || !Array.isArray(cfg.pieces)) return null;
    var bySlot = {};
    cfg.pieces.forEach(function (p) { if (p.slot && !bySlot[p.slot]) bySlot[p.slot] = p; });
    if (!bySlot.structure) return null;
    var qty = function (slot) { var p = bySlot[slot]; return p ? Math.max(0, Number(qtyOf(p.sku)) || 0) : 0; };
    var extOn = qty('extension') > 0 && !!bySlot.extension;
    var layoutName = extOn ? 'extension' : 'standard';
    var L = T.layouts[layoutName];
    var pictured = function (slot) {
      var cap = L.caps[slot];
      return Math.min(qty(slot), cap == null ? 0 : cap);
    };
    var nSc = pictured('scratcher'), nNest = pictured('nest'), nHouse = pictured('house');
    var nShelf = Math.min(pictured('shelf_small'), (L.shelf_small || []).length);
    var yOf = function (spec) { return (nSc === 0 && spec.y_no_scratcher != null) ? spec.y_no_scratcher : spec.y; };
    var xOf = function (slot, spec) {
      return T.uprights[spec.at] != null ? T.uprights[spec.at] - T.sizes[slot].w / 2 : T.sections[spec.at];
    };
    var item = function (slot, key, spec, extra) {
      var p = bySlot[slot];
      var sizeKey = slot === 'cushion_hemp' || slot === 'cushion_anthracite' ? 'cushion' : slot;
      var it = { sku: p.sku, slot: slot, key: key, at: spec.at, x: r2(xOf(sizeKey, spec)), y: r2(yOf(spec)), w: T.sizes[sizeKey].w, h: T.sizes[sizeKey].h };
      if (extra) Object.keys(extra).forEach(function (k) { it[k] = extra[k]; });
      return it;
    };

    // Drawing order mirrors the mockup: frame, extension, scratchers, small shelves, cushions, nests,
    // houses, big shelf.
    var items = [];
    items.push({ sku: bySlot.structure.sku, slot: 'structure', key: 'structure', x: T.frame.x, y: 0, w: T.frame.w, h: T.frame.h, ghost: qty('structure') === 0 });
    if (extOn) items.push({ sku: bySlot.extension.sku, slot: 'extension', key: 'extension', x: T.extension.x, y: 0, w: T.extension.w, h: T.extension.h });

    var k;
    if (bySlot.scratcher) for (k = 0; k < nSc; k++) items.push(item('scratcher', 'scratcher_' + (k + 1), L.scratcher[k]));

    // Small shelves: the first nShelf slots; every shelf after the first is a spare that fills the
    // Nest place (no Nest pictured) and then the House place (no House pictured). A moved shelf leaves
    // its own slot empty.
    var substitutions = [];
    var shelves = [];
    if (bySlot.shelf_small) {
      var specs = L.shelf_small.slice(0, nShelf).map(function (s) { return { at: s.at, y: s.y, y_no_scratcher: s.y_no_scratcher }; });
      var places = specs.map(function () { return null; });
      var spare = [];
      for (k = 1; k < specs.length; k++) spare.push(k);
      if (nNest === 0 && spare.length) { var a = spare.shift(); specs[a] = L.nest_place; places[a] = 'nest_place'; }
      if (nHouse === 0 && spare.length) { var b = spare.shift(); specs[b] = L.house_place; places[b] = 'house_place'; }
      specs.forEach(function (spec, idx) {
        var key = 'shelf_small_' + (idx + 1);
        var it = item('shelf_small', key, spec, places[idx] ? { place: places[idx] } : null);
        if (places[idx]) substitutions.push(key + ':' + places[idx]);
        shelves.push(it);
        items.push(it);
      });
    }

    // Cushions: shelf order, Hemp quantity first, one per pictured small shelf.
    var queue = [];
    T.cushion_order.forEach(function (slot) {
      if (!bySlot[slot]) return;
      for (var n = qty(slot); n > 0; n--) queue.push(slot);
    });
    shelves.forEach(function (shelf, idx) {
      var slot = queue[idx];
      if (!slot) return;
      items.push({
        sku: bySlot[slot].sku, slot: slot, key: 'cushion_' + (idx + 1),
        x: r2(shelf.x + T.cushion_on_shelf.dx), y: r2(shelf.y + T.cushion_on_shelf.dy),
        w: T.sizes.cushion.w, h: T.sizes.cushion.h, host: shelf.key
      });
    });

    if (bySlot.nest) for (k = 0; k < nNest; k++) items.push(item('nest', 'nest_' + (k + 1), L.nest[k]));
    if (bySlot.house) for (k = 0; k < nHouse; k++) items.push(item('house', 'house_' + (k + 1), L.house[k]));
    if (bySlot.shelf_big && pictured('shelf_big') >= 1) items.push(item('shelf_big', 'shelf_big', L.shelf_big));

    var D = T.drawing_box;
    return {
      items: items,
      extOn: extOn,
      layout: layoutName,
      substitutions: substitutions,
      toppersNotDrawn: Math.max(0, queue.length - shelves.length),
      shelvesPictured: shelves.length,
      bounds: { xmin: D.xmin, xmax: L.xmax, ymin: D.ymin, ymax: D.ymax }
    };
  }

  if (typeof window === 'undefined' || typeof customElements === 'undefined') {
    if (typeof module !== 'undefined') module.exports = { planWall: planWall, SLOT_TABLES: SLOT_TABLES };
    return;
  }
  if (customElements.get('dpc-wall-builder')) return;

  // ── Component ──────────────────────────────────────────────────────────────────────────────────
  // Module-scoped state, keyed by product handle, so a Prestige partial re-render (which replaces
  // the element's DOM) does not lose the shopper's quantities.
  var STATE = new Map(); // handle -> Map(variantId -> qty)

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Prices use the STORE's money format (data-money-format = Liquid shop.money_format; Prestige also
  // exposes themeVariables.settings.moneyFormat). Placeholders follow Shopify's own formatMoney.
  var MONEY_FORMAT = null;
  function formatMoney(cents, format) {
    var fmt = format || MONEY_FORMAT || (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '£{{amount}}';
    var t = document.createElement('textarea');
    t.innerHTML = String(fmt).replace(/<[^>]*>/g, '');
    fmt = t.value;
    var value = Math.round(Number(cents) || 0);
    function delimit(precision, thousands, decimal) {
      var parts = (value / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var out;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': out = delimit(0, ',', '.'); break;
      case 'amount_with_comma_separator': out = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = delimit(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = delimit(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = delimit(0, ' ', ''); break;
      case 'amount_with_space_separator': out = delimit(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': out = delimit(2, ' ', '.'); break;
      default: out = delimit(2, ',', '.');
    }
    return m ? fmt.replace(m[0], out) : out;
  }
  function money(cents) { return formatMoney(cents); }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function cm(n) { return String(Math.round(n * 10) / 10); }

  function qtyByVariant(cartJson) {
    var map = new Map();
    (cartJson && Array.isArray(cartJson.items) ? cartJson.items : []).forEach(function (l) {
      var vid = l.variant_id != null ? l.variant_id : l.id;
      map.set(vid, (map.get(vid) || 0) + (l.quantity || 0));
    });
    return map;
  }

  function fabric(piece) {
    return piece.variant_title && piece.variant_title.indexOf(':') > -1 ? piece.variant_title.split(':').pop().trim() : '';
  }
  function displayName(piece) {
    var f = fabric(piece);
    return piece.label + (f ? ', ' + f : '');
  }

  class DpcWallBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this._init();
      } catch (err) {
        // Fail closed: leave the native picker/ATC working, log for diagnosis, add nothing.
        console.error('dpc-wall-builder: failed to initialise, native controls remain', err);
      }
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      this._closePhoto(false);
    }

    _init() {
      var handle = this.dataset.productHandle;
      var cfgScript = this.querySelector('script[data-wb-config]');
      var varScript = this.querySelector('script[data-wb-variants]');
      if (!cfgScript || !varScript) return;

      var config = JSON.parse(cfgScript.textContent);
      var variants = JSON.parse(varScript.textContent);
      // v2 needs climb.table naming a slot table; without it (an older metafield) do not initialise,
      // so the native picker and Add to cart keep selling the product.
      if (!config || !Array.isArray(config.pieces) || !config.pieces.length || !config.climb || !SLOT_TABLES[config.climb.table]) return;

      this.config = config;
      this.handle = handle;
      this.assetBase = this.dataset.assetBase || '';
      if (this.dataset.moneyFormat) MONEY_FORMAT = this.dataset.moneyFormat;

      this.rows = config.pieces.map(function (piece) {
        var variant = variants.find(function (v) { return v.sku && piece.sku && v.sku === piece.sku; });
        if (!variant && piece.variant_title) {
          variant = variants.find(function (v) { return v.title === piece.variant_title; });
        }
        return { piece: piece, variant: variant || null };
      }).filter(function (row) { return row.variant; });

      if (!this.rows.length) return;

      if (!STATE.has(handle)) STATE.set(handle, new Map());
      this.qty = STATE.get(handle);

      this._cacheEls();
      // Visible from here on (the inline style hides the block until it is ready), so the stage has a
      // real size for the first picture fit.
      this.classList.add('is-ready');
      this._renderList();
      this._applyVariantPreset();
      this._renderPicture();
      this._renderTotals();
      this._wireEvents();

      // Only now hide the native controls and reveal the jump button: a successful init is the gate.
      document.body.classList.add('dpc-wall-builder-on');
      var self = this;
      document.querySelectorAll('[data-wb-jump]').forEach(function (a) {
        if (a.getAttribute('data-wb-jump') !== self.id) return;
        a.hidden = false;
        if (a._wbBound) return;
        a._wbBound = true;
        a.addEventListener('click', function (evt) {
          var target = document.getElementById(a.getAttribute('data-wb-jump'));
          if (!target) return;
          evt.preventDefault();
          target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          var title = target.querySelector('[data-wb-title]');
          if (title) title.focus({ preventScroll: true });
        });
      });
    }

    _cacheEls() {
      this.$list = this.querySelector('[data-wb-list]');
      this.$wall = this.querySelector('[data-wb-wall]');
      this.$stage = this.querySelector('[data-wb-stage]') || (this.$wall && this.$wall.parentElement);
      this.$summary = this.querySelector('[data-wb-summary]');
      this.$barCount = this.querySelector('[data-wb-bar-count]');
      this.$barTotal = this.querySelector('[data-wb-bar-total]');
      this.$add = this.querySelector('[data-wb-add]');
      this.$status = this.querySelector('[data-wb-status]');
      this.$picture = this.querySelector('[data-wb-picture]');
    }

    _extOn() {
      var self = this;
      var ext = this.rows.find(function (r) { return r.piece.slot === 'extension'; });
      return !!ext && (self.qty.get(ext.variant.id) || 0) > 0;
    }

    _hintFor(piece) {
      return this._extOn() && piece.hint_ext ? piece.hint_ext : (piece.hint || '');
    }

    _renderList() {
      var self = this;
      this.$list.innerHTML = '';
      this.rows.forEach(function (row) {
        var variant = row.variant, piece = row.piece;
        var qty = self.qty.get(variant.id) || 0;
        var name = displayName(piece);
        var li = document.createElement('li');
        li.className = 'dpc-wb__row';
        li.dataset.variantId = variant.id;
        li.dataset.sku = piece.sku;

        if (variant.thumb && variant.photo) {
          var thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'dpc-wb__thumb';
          thumb.setAttribute('aria-label', 'View photo of ' + name);
          thumb.setAttribute('aria-haspopup', 'dialog');
          var timg = document.createElement('img');
          timg.src = variant.thumb;
          timg.alt = '';
          timg.loading = 'lazy';
          timg.width = 56;
          timg.height = 56;
          var tag = document.createElement('span');
          tag.className = 'dpc-wb__thumb-tag';
          tag.setAttribute('aria-hidden', 'true');
          tag.textContent = 'View';
          thumb.appendChild(timg);
          thumb.appendChild(tag);
          thumb.addEventListener('click', function () { self._openPhoto(row, thumb); });
          li.appendChild(thumb);
        } else {
          var spacer = document.createElement('span');
          spacer.className = 'dpc-wb__thumb dpc-wb__thumb--empty';
          spacer.setAttribute('aria-hidden', 'true');
          li.appendChild(spacer);
        }

        var info = document.createElement('div');
        info.className = 'dpc-wb__row-info';
        var label = document.createElement('p');
        label.className = 'dpc-wb__row-label';
        label.textContent = name;
        info.appendChild(label);
        if (!variant.available) {
          var oos = document.createElement('p');
          oos.className = 'dpc-wb__row-oos';
          oos.textContent = 'Currently out of stock';
          info.appendChild(oos);
        }
        var hint = document.createElement('p');
        hint.className = 'dpc-wb__row-note';
        hint.dataset.wbHint = '';
        hint.textContent = self._hintFor(piece);
        if (!hint.textContent) hint.hidden = true;
        info.appendChild(hint);
        var price = document.createElement('p');
        price.className = 'dpc-wb__row-price';
        price.textContent = money(variant.price);
        info.appendChild(price);
        li.appendChild(info);

        var stepper = document.createElement('div');
        stepper.className = 'dpc-wb__stepper';
        var minus = document.createElement('button');
        minus.type = 'button';
        minus.setAttribute('aria-label', 'Remove one ' + name);
        minus.textContent = '−';
        minus.disabled = qty === 0;
        var out = document.createElement('output');
        out.textContent = String(qty);
        out.setAttribute('aria-label', name + ' quantity');
        var plus = document.createElement('button');
        plus.type = 'button';
        plus.setAttribute('aria-label', 'Add one ' + name);
        plus.textContent = '+';
        plus.disabled = !variant.available;

        minus.addEventListener('click', function () { self._setQty(variant.id, Math.max(0, (self.qty.get(variant.id) || 0) - 1)); });
        plus.addEventListener('click', function () { self._setQty(variant.id, (self.qty.get(variant.id) || 0) + 1); });

        stepper.appendChild(minus);
        stepper.appendChild(out);
        stepper.appendChild(plus);
        li.appendChild(stepper);

        row._li = li;
        row._out = out;
        row._minus = minus;
        row._hint = hint;
        self.$list.appendChild(li);
      });
    }

    _refreshHints() {
      var self = this;
      this.rows.forEach(function (row) {
        if (!row._hint) return;
        row._hint.textContent = self._hintFor(row.piece);
        row._hint.hidden = !row._hint.textContent;
      });
    }

    // ── Lightbox: the real product photo (the variant's featured image) ──
    _openPhoto(row, trigger) {
      var self = this;
      this._closePhoto(false);
      var name = displayName(row.piece);
      var uid = 'dpc-wb-photo-' + (this.id || 'x');
      var backdrop = document.createElement('div');
      backdrop.className = 'dpc-wb-lightbox';
      backdrop.setAttribute('data-wb-lightbox', '');
      var dialog = document.createElement('div');
      dialog.className = 'dpc-wb-lightbox__dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', uid + '-title');
      var img = document.createElement('img');
      img.className = 'dpc-wb-lightbox__img';
      img.src = row.variant.photo;
      img.alt = name;
      img.setAttribute('data-wb-lightbox-img', '');
      var meta = document.createElement('div');
      meta.className = 'dpc-wb-lightbox__meta';
      var text = document.createElement('div');
      var title = document.createElement('p');
      title.className = 'dpc-wb-lightbox__title';
      title.id = uid + '-title';
      title.textContent = name;
      var sub = document.createElement('p');
      sub.className = 'dpc-wb-lightbox__sub';
      sub.textContent = 'Product photo, ' + money(row.variant.price);
      text.appendChild(title);
      text.appendChild(sub);
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'dpc-wb-lightbox__close';
      close.setAttribute('data-wb-lightbox-close', '');
      close.textContent = 'Close';
      meta.appendChild(text);
      meta.appendChild(close);
      dialog.appendChild(img);
      dialog.appendChild(meta);
      backdrop.appendChild(dialog);

      backdrop.addEventListener('click', function (evt) { if (evt.target === backdrop) self._closePhoto(true); });
      close.addEventListener('click', function () { self._closePhoto(true); });
      this._onKey = function (evt) {
        if (evt.key === 'Escape') { evt.preventDefault(); evt.stopPropagation(); self._closePhoto(true); }
        else if (evt.key === 'Tab') { evt.preventDefault(); evt.stopPropagation(); close.focus(); } // the only focusable control
      };
      // Keep focus in the dialog even when another page overlay (e.g. Shopify's cookie consent banner,
      // which manages its own focus) tries to take it. Window + capture runs before page handlers.
      this._onFocusIn = function (evt) {
        if (self._photo && !dialog.contains(evt.target)) close.focus();
      };
      window.addEventListener('keydown', this._onKey, true);
      document.addEventListener('focusin', this._onFocusIn, true);
      this._photo = { el: backdrop, trigger: trigger };
      document.body.appendChild(backdrop);
      document.documentElement.classList.add('dpc-wb-lightbox-open');
      close.focus();
    }

    _closePhoto(returnFocus) {
      if (!this._photo) return;
      var p = this._photo;
      this._photo = null;
      window.removeEventListener('keydown', this._onKey, true);
      document.removeEventListener('focusin', this._onFocusIn, true);
      if (p.el.parentNode) p.el.parentNode.removeChild(p.el);
      document.documentElement.classList.remove('dpc-wb-lightbox-open');
      if (returnFocus && p.trigger && p.trigger.isConnected) p.trigger.focus();
    }

    _setQty(variantId, qty) {
      this.qty.set(variantId, qty);
      var row = this.rows.find(function (r) { return r.variant.id === variantId; });
      if (row) {
        row._out.textContent = String(qty);
        row._minus.disabled = qty === 0;
        if (row.piece.slot === 'extension') this._refreshHints();
      }
      this._renderPicture();
      this._renderTotals();
      this.$add.classList.remove('is-added');
      this.$add.textContent = 'Add all to basket';
    }

    _renderTotals() {
      var self = this;
      var count = 0, total = 0;
      this.rows.forEach(function (row) {
        var q = self.qty.get(row.variant.id) || 0;
        count += q;
        total += q * row.variant.price;
      });
      this.$barCount.textContent = count + (count === 1 ? ' piece' : ' pieces');
      this.$barTotal.textContent = money(total);
      var disabled = count === 0;
      this.$add.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if (count === 0) this._announce('Use plus to choose at least one piece');
      else this._announce(count + (count === 1 ? ' piece' : ' pieces') + ', ' + money(total), true);
    }

    // repeatsBar: the message only repeats the basket bar's count and total, so it is announced to screen
    // readers but visually hidden (.is-repeat in dpc-wall-builder.css; Martin 2026-09-14). Errors and every
    // other message stay visible. Class and text change together, in the debounced call.
    _announce(text, repeatsBar) {
      var self = this;
      if (!this._announceDebounced) {
        this._announceDebounced = debounce(function (t, repeat) {
          self.$status.classList.toggle('is-repeat', !!repeat);
          self.$status.textContent = t;
        }, 400);
      }
      this._announceDebounced(text, !!repeatsBar);
    }

    _qtyBySku() {
      var self = this, map = {};
      this.rows.forEach(function (r) { map[r.piece.sku] = self.qty.get(r.variant.id) || 0; });
      return map;
    }

    _renderPicture() {
      var self = this;
      var bySku = this._qtyBySku();
      var plan = planWall(this.config, function (sku) { return bySku[sku] || 0; });
      if (!plan) return;
      this._plan = plan;
      var B = plan.bounds, W = B.xmax - B.xmin, H = B.ymax - B.ymin;
      this._ratio = W / H;
      this.$wall.dataset.xmin = String(B.xmin);
      this.$wall.dataset.xmax = String(B.xmax);
      this.$wall.dataset.ymax = String(B.ymax);
      this._fitWallBox();
      this.$wall.innerHTML = '';

      var pieceBySku = {};
      this.config.pieces.forEach(function (p) { pieceBySku[p.sku] = p; });
      var Z = { structure: 1, extension: 1, scratcher: 2, shelf_small: 3, cushion_hemp: 4, cushion_anthracite: 4, nest: 5, house: 6, shelf_big: 7 };
      plan.items.forEach(function (it) {
        var piece = pieceBySku[it.sku];
        var el = self._pieceEl(piece, it);
        el.style.setProperty('--x', ((it.x - B.xmin) / W) * 100 + '%');
        el.style.setProperty('--y', ((it.y - B.ymin) / H) * 100 + '%');
        el.style.setProperty('--w', (it.w / W) * 100 + '%');
        el.style.setProperty('--h', (it.h / H) * 100 + '%');
        el.style.zIndex = String(Z[it.slot] || 3);
        el.dataset.xCm = String(it.x);
        el.dataset.yCm = String(it.y);
        el.dataset.wCm = String(it.w);
        el.dataset.hCm = String(it.h);
        el.dataset.slot = it.key;
        if (it.place) el.dataset.place = it.place;
        if (it.host) el.dataset.host = it.host;
        el.classList.add(it.ghost ? 'is-ghost' : 'is-on');
        self.$wall.appendChild(el);
      });

      var built = [];
      this.rows.forEach(function (row) {
        var n = self.qty.get(row.variant.id) || 0;
        if (n > 0) built.push(n > 1 ? n + ' × ' + displayName(row.piece) : displayName(row.piece));
      });
      var notes = [];
      if (plan.toppersNotDrawn > 0) {
        notes.push(plan.shelvesPictured === 0 ? 'Cushions are shown once a shelf is chosen.' : 'Each small shelf shows one cushion.');
      }
      if (this.$summary) {
        this.$summary.textContent = notes.join(' ');
        this.$summary.hidden = notes.length === 0;
      }
      this.$picture.setAttribute('aria-label', this._title() + ': ' + (built.length ? built.join(', ') : 'empty wall'));
    }

    // Fit the wall (bounds ratio) inside the stage box, "contain" sizing. The stage's size is set by
    // CSS (a fixed-height pinned box on phones, a viewport-bound box on desktop); a ResizeObserver
    // refits on any change.
    _fitWallBox() {
      var stage = this.$stage;
      if (!stage) return;
      if (!this._ro && window.ResizeObserver) {
        var self = this;
        this._ro = new ResizeObserver(function () { self._fitWallBox(); });
        this._ro.observe(stage);
      }
      var cs = window.getComputedStyle(stage);
      var w = stage.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      var h = stage.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
      if (!w || !h || !this._ratio) return;
      var boxW = w, boxH = w / this._ratio;
      if (boxH > h) { boxH = h; boxW = h * this._ratio; }
      this.$wall.style.width = Math.floor(boxW) + 'px';
      this.$wall.style.height = Math.floor(boxH) + 'px';
    }

    _title() {
      return this.dataset.productTitle || 'Wall system';
    }

    _pieceEl(piece, it) {
      var el = document.createElement('div');
      el.className = 'dpc-wb__piece';
      el.dataset.piece = piece.sku;
      el.dataset.role = piece.slot;
      if (DpcWallBuilder.isPhoto(piece)) {
        el.dataset.render = 'photo';
        var img = document.createElement('img');
        img.src = /^https?:/.test(piece.asset) ? piece.asset : (this.assetBase + piece.asset);
        img.alt = '';
        img.decoding = 'async';
        el.appendChild(img);
      } else {
        el.dataset.render = 'outline';
        el.classList.add('dpc-wb__piece--outline');
        if (piece.shape === 'round') el.classList.add('dpc-wb__piece--round');
        var span = document.createElement('span');
        var dims = piece.elevation_dims_verified === false
          ? cm(piece.w_cm) + 'cm wide'
          : cm(piece.w_cm) + '×' + cm(piece.h_cm) + 'cm';
        var ymax = Number(this.$wall.dataset.ymax) || 190;
        var boxPx = (it.h / ymax) * (parseFloat(this.$wall.style.height) || 320);
        el.title = displayName(piece) + ', ' + dims;
        if (it.host) {
          el.classList.add('dpc-wb__piece--thin');
          span.textContent = fabric(piece) || piece.label;
        } else if (boxPx < 28) {
          el.classList.add('dpc-wb__piece--thin');
          span.textContent = piece.label + ', ' + dims;
        } else {
          span.textContent = piece.label + '\n' + dims;
        }
        el.appendChild(span);
      }
      return el;
    }

    // A piece renders as a PHOTO only when its manifest status is exactly 'PASS' and it has an asset.
    static isPhoto(piece) {
      return piece.grade === 'photo' && piece.manifest_status === 'PASS' && !!piece.asset;
    }

    // Reusable never-stack validator for a free-mount system (Parete/Gradino). Not called by Alto.
    static wouldStack(placed, candidate) {
      return placed.some(function (p) {
        var overlapX = Math.min(p.x_cm + p.w_cm, candidate.x_cm + candidate.w_cm) - Math.max(p.x_cm, candidate.x_cm);
        var narrower = Math.min(p.w_cm, candidate.w_cm);
        return overlapX > narrower / 2;
      });
    }

    _applyVariantPreset() {
      var params = new URLSearchParams(window.location.search);
      var presetId = params.get('variant');
      if (!presetId) return;
      var row = this.rows.find(function (r) { return String(r.variant.id) === String(presetId); });
      if (!row) return;
      this.qty.set(row.variant.id, Math.max(1, this.qty.get(row.variant.id) || 0));
      var self = this;
      requestAnimationFrame(function () {
        self._renderList();
        self._renderPicture();
        self._renderTotals();
        if (row._li) row._li.classList.add('is-preset');
      });
    }

    _wireEvents() {
      var self = this;
      this.$add.addEventListener('click', function () {
        if (self.$add.getAttribute('aria-disabled') === 'true') {
          self._announce('Use plus to choose at least one piece');
          return;
        }
        self._addToCart();
      });
    }

    async _addToCart() {
      var self = this;
      var items = [];
      this.rows.forEach(function (row) {
        var q = self.qty.get(row.variant.id) || 0;
        if (q > 0) items.push({ id: row.variant.id, quantity: q });
      });
      if (!items.length) return;

      this.$add.setAttribute('aria-busy', 'true');
      this.$add.disabled = true;
      this.$add.textContent = 'Adding…';

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      var sectionsUrl = window.location.pathname;

      var before = null;
      try {
        before = qtyByVariant(await (await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })).json());
      } catch (e) {
        before = null;
      }

      try {
        var res = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items, sections: sectionsToBundle, sections_url: sectionsUrl })
        });

        var cartJson, sectionsHtml;
        if (res.ok) {
          var addJson = await res.json();
          sectionsHtml = addJson.sections;
          cartJson = await (await fetch(root() + 'cart.js')).json();
          this._reportLanded(items, cartJson, true, before);
        } else {
          // PR #28 review: read Shopify's own reason first. Only a 422 is a stock/quantity refusal.
          var errJson = null;
          try { errJson = await res.json(); } catch (e) { errJson = null; }
          var updRes = await fetch(root() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: {}, sections: sectionsToBundle, sections_url: sectionsUrl })
          });
          cartJson = await updRes.json();
          sectionsHtml = cartJson.sections;
          this._reportLanded(items, cartJson, false, before, res.status, errJson && (errJson.description || errJson.message));
        }

        if (cartJson) {
          cartJson.sections = sectionsHtml || {};
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartJson }
          }));
        }

        if (res.ok) {
          this.$add.classList.add('is-added');
          this.$add.textContent = 'Added to basket';
        } else {
          this.$add.textContent = 'Add all to basket';
        }
      } catch (err) {
        console.error('dpc-wall-builder: add failed', err);
        this._announce('Something went wrong adding these pieces. Please try again.');
        this.$add.textContent = 'Add all to basket';
      } finally {
        this.$add.removeAttribute('aria-busy');
        this.$add.disabled = false;
      }
    }

    _reportLanded(requestedItems, cartJson, allOk, before, status, reason) {
      var self = this;
      if (allOk) {
        this._announce('Added to basket.');
        return;
      }
      // PR #28 review: a 429/5xx/network-style failure is not a stock problem, so never word it as one.
      if (status !== 422) {
        this._announce('Something went wrong adding these pieces. Please try again, or check your basket.');
        return;
      }
      var why = typeof reason === 'string' ? reason.trim() : '';
      if (!before || !cartJson || !Array.isArray(cartJson.items)) {
        this._announce((why ? why + ' ' : 'Some pieces could not be added. ') + 'Please check your basket.');
        return;
      }
      var after = qtyByVariant(cartJson);
      var short = [];
      var anyLanded = false;
      requestedItems.forEach(function (req) {
        var landed = Math.max(0, (after.get(req.id) || 0) - (before.get(req.id) || 0));
        if (landed > 0) anyLanded = true;
        var row = self.rows.find(function (r) { return r.variant.id === req.id; });
        if (landed < req.quantity && row) {
          short.push(landed + ' of ' + req.quantity + ' ' + row.piece.label);
        }
      });
      // Nothing landed: the whole request was refused, so a per-piece shortfall would be false.
      if (!anyLanded) {
        this._announce((why || 'These pieces could not be added.') + ' Nothing was added to your basket.');
        return;
      }
      this._announce(short.length
        ? 'Only ' + short.join('; ') + ' could be added.' + (why ? ' ' + why : '')
        : (why ? why + ' ' : 'Some pieces could not be added. ') + 'Please check your basket.');
    }
  }

  customElements.define('dpc-wall-builder', DpcWallBuilder);

  // Exposed for the regression suite and the probe only (never used by the component at runtime).
  window.__dpcWallBuilderTestHooks = {
    wouldStack: DpcWallBuilder.wouldStack,
    formatMoney: function (c) { return formatMoney(c); },
    planWall: planWall
  };
})();
